import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { staggerContainer, staggerItem } from '../lib/animations';
import Avatar from '../components/Avatar';
import ScoreTrend from '../components/ScoreTrend';
import AnimatedButton from '../components/AnimatedButton';
import AnimatedCounter from '../components/AnimatedCounter';

interface Badge {
  emoji: string;
  name: string;
  desc: string;
}

interface HistoryItem {
  id: number;
  final_score: number;
  verdict: string;
  image_path: string;
  created_at: string;
}

interface ProfileData {
  username: string;
  stats: {
    totalRolls: number;
    avgScore: number;
    bestScore: number;
    worstScore: number;
  };
  badges: Badge[];
  history: {
    items: HistoryItem[];
    page: number;
    pageSize: number;
    totalItems: number;
  };
}

export default function Profile() {
  const { name: username } = useParams<{ name: string }>();
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [page, setPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const pageSize = 10;

  useEffect(() => {
    if (!username) return;
    setLoading(true);
    setNotFound(false);
    setPage(1);
    fetch(`/api/profile/${encodeURIComponent(username)}?page=1&pageSize=${pageSize}`)
      .then((r) => {
        if (r.status === 404) { setNotFound(true); setLoading(false); return null; }
        return r.json();
      })
      .then((data) => { if (data) setProfile(data); })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [username]);

  const hasMore = profile
    ? page * pageSize < profile.history.totalItems
    : false;

  const loadMore = useCallback(async () => {
    if (!username || loadingMore) return;
    const nextPage = page + 1;
    setLoadingMore(true);
    try {
      const res = await fetch(`/api/profile/${encodeURIComponent(username)}?page=${nextPage}&pageSize=${pageSize}`);
      const data = await res.json();
      setProfile((prev) =>
        prev ? {
          ...prev,
          history: {
            ...data.history,
            items: [...prev.history.items, ...data.history.items],
          },
        } : prev
      );
      setPage(nextPage);
    } catch { /* ignore */ }
    setLoadingMore(false);
  }, [username, page, loadingMore]);

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto px-3 py-4 animate-fade-in">
        {/* Header skeleton */}
        <div className="flex items-center gap-3 mb-5">
          <div className="w-12 h-12 rounded-full skeleton" />
          <div className="flex-1 space-y-1.5">
            <div className="h-4 w-28 skeleton" />
            <div className="h-3 w-40 skeleton" />
          </div>
        </div>
        {/* Stats skeleton */}
        <div className="grid grid-cols-2 gap-2 mb-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="bg-cannabis-800 rounded-2xl p-3 border border-cannabis-700/30">
              <div className="h-4 w-5 skeleton mb-1.5" />
              <div className="h-5 w-14 skeleton mb-1" />
              <div className="h-3 w-16 skeleton" />
            </div>
          ))}
        </div>
        {/* Trend skeleton */}
        <div className="bg-cannabis-800 rounded-2xl p-3 border border-cannabis-700/30 mb-4">
          <div className="h-3 w-20 skeleton mb-2" />
          <div className="h-[100px] skeleton" />
        </div>
      </div>
    );
  }

  if (notFound || !profile) {
    return (
      <div className="max-w-2xl mx-auto px-3 py-5 text-center">
        <h2 className="text-xl font-bold text-cream mb-2">Roller Not Found</h2>
        <p className="text-cream/40 mb-4 text-sm">No profile exists for "{username}"</p>
        <Link to="/" className="text-cannabis-400 hover:text-cannabis-300 transition underline text-sm">
          Back to Home
        </Link>
      </div>
    );
  }

  const historyItems = profile.history.items;
  // Reverse for chronological order (API returns newest-first, chart needs oldest-left)
  const trendScores = [...historyItems].reverse().map((h) => ({ score: h.final_score, date: h.created_at }));
  // Derive member_since from the oldest rating in history (last item since API is newest-first)
  const memberSince = historyItems.length > 0 ? historyItems[historyItems.length - 1].created_at : null;

  return (
    <div className="max-w-2xl mx-auto px-3 py-4">
      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <Avatar username={profile.username} size="lg" />
        <div>
          <h2 className="text-xl font-bold text-cream">{profile.username}</h2>
          {memberSince && (
            <p className="text-cream/35 text-xs">
              Member since {new Date(memberSince).toLocaleDateString(undefined, { year: 'numeric', month: 'long' })}
            </p>
          )}
        </div>
      </div>

      {/* Stats Grid */}
      <motion.div className="grid grid-cols-2 gap-2 mb-4" variants={staggerContainer} initial="initial" animate="animate">
        {[
          { label: 'Total Rolls', value: profile.stats.totalRolls, decimals: 0 },
          { label: 'Avg Score', value: profile.stats.avgScore, decimals: 2 },
          { label: 'Best Score', value: profile.stats.bestScore, decimals: 2 },
          { label: 'Worst Score', value: profile.stats.worstScore, decimals: 2 },
        ].map((stat) => (
          <motion.div key={stat.label} variants={staggerItem} className="bg-cannabis-800 rounded-2xl p-3 border border-cannabis-700/30 shadow-card">
            <div className="text-cannabis-300 font-mono font-bold text-lg">
              <AnimatedCounter value={stat.value} decimals={stat.decimals} />
            </div>
            <div className="text-cream/35 text-[11px]">{stat.label}</div>
          </motion.div>
        ))}
      </motion.div>

      {/* Badges */}
      {profile.badges.length > 0 && (
        <div className="mb-4">
          <h3 className="text-cream/50 text-xs font-semibold mb-1.5">Badges</h3>
          <div className="flex flex-wrap gap-1.5">
            {profile.badges.map((b, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-cannabis-700 text-cannabis-200 border border-cannabis-600/50"
                title={b.desc}
              >
                <span>{b.emoji}</span>
                <span>{b.name}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Score Trend */}
      <div className="bg-cannabis-800 rounded-2xl p-3 border border-cannabis-700/30 shadow-card mb-4">
        <h3 className="text-cream/50 text-xs font-semibold mb-2">Score Trend</h3>
        <ScoreTrend scores={trendScores} />
      </div>

      {/* Rating History */}
      <div>
        <h3 className="text-cream/50 text-xs font-semibold mb-2">Rating History</h3>
        {historyItems.length === 0 ? (
          <p className="text-cream/40 text-center py-6 text-sm">No ratings yet.</p>
        ) : (
          <div className="space-y-2">
            {historyItems.map((r) => (
              <div key={r.id} className="flex items-start gap-2.5 bg-cannabis-800 rounded-2xl p-3 border border-cannabis-700/30 shadow-card">
                {r.image_path && (
                  <img
                    src={r.image_path}
                    alt=""
                    className="w-14 h-14 object-cover rounded-xl shrink-0"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="text-cannabis-400 font-mono font-bold text-sm">{r.final_score.toFixed(2)}</span>
                    <span className="text-cream/25 text-[11px]">
                      {new Date(r.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-cream/50 italic text-xs line-clamp-2">"{r.verdict}"</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {hasMore && (
          <div className="mt-3">
            <AnimatedButton onClick={loadMore} loading={loadingMore} variant="secondary" size="sm" className="w-full">
              Load More
            </AnimatedButton>
          </div>
        )}
      </div>
    </div>
  );
}
